import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { bridge } from "@multiweb/reactCore/bridge";
import {
    AttributeSelectorParams,
    getActions,
    getAttributeTypeByAttributeName,
    getConfigAttributeByName,
    getFulltextSearchString,
    getRecordValueById,
    isFulltextConditionActive,
    RecordAttributeSelectorParams,
} from "@multiweb/reactCore/datasource";
import { getPrimaryText, getSecondaryText, UNKNOWN_KEY_TEXT } from "@multiweb/reactCore/i18n";
import { IconCode, OWDataType } from "@multiweb/types/contracts";
import { IAppState, IEmptyMessage } from "@multiweb/types/react";
import { LOC_KEY_NO, LOC_KEY_YES } from "./locKeys";
import { EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT } from "./reducer";
import { ActionArea, EmptyMessageSettings, IFramesState, RowTemplateType } from "./types";

const { applyLocParams, generateUUID } = bridge;
/**
 * Získá z globálního stavu frame podle `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getFrameInstance: Selector<IAppState, Readonly<IFramesState> | undefined, string> = (
    state: IAppState,
    frameKey: string,
) => state.frames[frameKey];

/**
 * Získá **emptyMessageCustomization** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací defaultní hodnoty.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getEmptyMessageCustomization: Selector<IAppState, EmptyMessageSettings, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.emptyMessageCustomization ?? EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT;

/**
 * Získá překlad **noItemsLocKey** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací defaultní text.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getNoItemsText: Selector<IAppState, string, string> = (state, frameKey) =>
    getPrimaryText(state, getEmptyMessageCustomization(state, frameKey).noItemsLocKey) ||
    getPrimaryText(state, EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsLocKey) ||
    UNKNOWN_KEY_TEXT;

/**
* Získá překlad **noItemsIcon** pro frame uložený pod klíčem `frameKey`.
* Pokud pro daný klíč neexistuje instance, vrací defaultní ikonu.
*
* @param state
* @param frameKey Klíč framu.
*/
export const getNoItemsIcon: Selector<IAppState, IconCode, string> = (state, frameKey) =>
    getEmptyMessageCustomization(state, frameKey).noItemsIcon;

/**
* Získá překlad **noItemsFilterText** pro frame uložený pod klíčem `frameKey`.
* Pokud pro daný klíč neexistuje instance, vrací defaultní text.
*
* @param state
* @param frameKey Klíč framu.
*/
export const getNoItemsFilterText: Selector<IAppState, string, string> = (state, frameKey) =>
    getPrimaryText(state, getEmptyMessageCustomization(state, frameKey).noItemsFilterLocKey) ||
    getPrimaryText(state, EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsFilterLocKey) ||
    UNKNOWN_KEY_TEXT;

/**
* Získá překlad **noItemsFilterIcon** pro frame uložený pod klíčem `frameKey`.
* Pokud pro daný klíč neexistuje instance, vrací defaultní ikonu.
*
* @param state
* @param frameKey Klíč framu.
*/
export const getNoItemsFilterIcon: Selector<IAppState, IconCode, string> = (state, frameKey) =>
    getEmptyMessageCustomization(state, frameKey).noItemsFilterIcon;

/**
* Získá překlad **noItemsFulltextSearchText** pro frame uložený pod klíčem `frameKey`.
* Pokud pro daný klíč neexistuje instance, vrací defaultní text.
*
* @param state
* @param frameKey Klíč framu.
*/
export const getNoItemsFulltextSearchText: Selector<IAppState, string, string> = (state, frameKey) =>
    getPrimaryText(state, getEmptyMessageCustomization(state, frameKey).noItemsFulltextSearchLocKey) ||
    getPrimaryText(state, EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsFulltextSearchLocKey) ||
    UNKNOWN_KEY_TEXT;

/**
* Získá překlad **noItemsFulltextSearchIcon** pro frame uložený pod klíčem `frameKey`.
* Pokud pro daný klíč neexistuje instance, vrací defaultní ikonu.
*
* @param state
* @param frameKey Klíč framu.
*/
export const getNoItemsFulltextSearchIcon: Selector<IAppState, IconCode, string> = (state, frameKey) =>
    getEmptyMessageCustomization(state, frameKey).noItemsFulltextSearchIcon;

/**
 * Vytvoří selector, který na základě **isFulltextConditionActive** získá informaci, že **fulltextCondition** existuje a není prázdná.
 * Je-li tomu tak, vrací typ **EmptyMessage** s příslušnou ikonou a informací o tom, že data nebyla nalezena pro hledaný text.
 * V opačném případě vrací ikonu a informaci o tom, že data nebyla nalezena při inicializaci.
 */
export const createGetEmptyMessageSelector = () => createSelector<
    IAppState,
    string,
    ReturnType<typeof getFulltextSearchString>,
    ReturnType<typeof isFulltextConditionActive>,
    ReturnType<typeof getNoItemsText>,
    ReturnType<typeof getNoItemsIcon>,
    ReturnType<typeof getNoItemsFulltextSearchText>,
    ReturnType<typeof getNoItemsFulltextSearchIcon>,
    IEmptyMessage | undefined
>(
    getFulltextSearchString,
    isFulltextConditionActive,
    getNoItemsText,
    getNoItemsIcon,
    getNoItemsFulltextSearchText,
    getNoItemsFulltextSearchIcon,
    (fulltextSearchString, isFulltextConditionActive, noItemsText, noItemsIcon, fulltextNoItemsText, fulltextNoItemsIcon) => {
        if (isFulltextConditionActive) {
            const localizedMessage =
                applyLocParams(fulltextNoItemsText || "", [fulltextSearchString ?? ""]) ||
                UNKNOWN_KEY_TEXT;

            return {
                text: localizedMessage,
                iconCode: fulltextNoItemsIcon,
            };
        } else {
            return {
                text: noItemsText || "",
                iconCode: noItemsIcon,
            };
        }
    },
);

/**
 * Získá **canValueLoad** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getCanValueLoad: Selector<IAppState, boolean | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.canValueLoad ?? undefined;

/**
 * Získá **actionArea** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getActionArea: Selector<IAppState, ActionArea | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.actionArea;

/**
 * Získá **rowTemplate** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getRowTemplate: Selector<IAppState, RowTemplateType | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.rowTemplate;

/**
 * Získá **showHeader** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getShowHeader: Selector<IAppState, boolean | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.showHeader;

/**
 * Získá **loadOnScroll** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getLoadOnScroll: Selector<IAppState, boolean | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.loadOnScroll;

/**
 * Získá **allowMultiselectMode** z framu získeho pomocí `frameKey`.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getAllowMultiselectMode: Selector<IAppState, boolean | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.allowMultiselect;

/**
 * Získá **interactive** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getInteractive: Selector<IAppState, boolean | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.interactive;

/**
 * Vytvoří selector, který vrací unikátní klíč při změně parametrů, který by měly zajistit vyčištění cache ve virtualized listu.
 */
export const createGetRowRerenderKey = () =>
    createSelector<
        IAppState,
        string,
        ReturnType<typeof getActionArea>,
        ReturnType<typeof getRowTemplate>,
        ReturnType<typeof getActions>,
        string | undefined
    >(
        getActionArea,
        getRowTemplate,
        getActions,
        (actionArea, rowTemplate, actions) => {
            if (actionArea && rowTemplate && actions) {
                return generateUUID();
            }
            return undefined;
        },
    );

/**
 * Získá lokalizovaný hlavní text pro zadaný attributeName.
 *
 * @param state
 * @param attributeSelectorParams Obsahuje klíč k instanci DataSource a název atributu.
 * @returns
 */
export const getAtributePrimaryText: Selector<IAppState, string | undefined, AttributeSelectorParams> = (state, { dataSourceKey, attributeName }) =>
    getPrimaryText(state, getConfigAttributeByName(state, { dataSourceKey, attributeName })?.locKey ?? "");

/**
 * Získá lokalizovaný alternativní text pro zadaný attributeName.
 *
 * @param state
 * @param attributeSelectorParams Obsahuje klíč k instanci DataSource a název atributu.
 * @returns
 */
export const getAtributeSecondaryText: Selector<IAppState, string | undefined, AttributeSelectorParams> = (state, { dataSourceKey, attributeName }) =>
    getSecondaryText(state, getConfigAttributeByName(state, { dataSourceKey, attributeName })?.locKey ?? "");

/**
 * Získá **visibility** pro frame uložený pod klíčem `frameKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param frameKey Klíč framu.
 */
export const getVisibility: Selector<IAppState, boolean | undefined, string> = (state, frameKey) =>
    getFrameInstance(state, frameKey)?.visibility;

/**
 * Získá **title** pro záznam.
 * Pokud pro daný klíč neexistuje titulek, vrací prázdný řetězec.
 */
export const createGetRecordTitleById = () => createSelector<
    IAppState,
    RecordAttributeSelectorParams,
    ReturnType<typeof getPrimaryText>,
    ReturnType<typeof getPrimaryText>,
    ReturnType<typeof getAtributeSecondaryText>,
    ReturnType<typeof getAttributeTypeByAttributeName>,
    ReturnType<typeof getRecordValueById>,
    string
>(
    (state) => getPrimaryText(state, LOC_KEY_YES) ?? "",
    (state) => getPrimaryText(state, LOC_KEY_NO) ?? "",
    (state, params) => getAtributeSecondaryText(state, params) ?? "",
    (state, params) => getAttributeTypeByAttributeName(state, params),
    (state, params) => getRecordValueById(state, params),
    (yesText, noText, secondaryText, attributeType, value) => {
        let title: string | undefined;

        if (attributeType === OWDataType.boolean) {
            if (value === true)
                title = yesText;
            else if (value === false)
                title = noText;
        } else {
            title = secondaryText;
        }
        return title ?? "";
    },
);
